import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, PartyPopper } from "lucide-react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { DashboardStats } from "@/types/dashboard";

const STORAGE_KEY = "quiznudge-checklist-hidden";

interface WelcomeChecklistProps {
  data: DashboardStats | undefined;
  isLoading: boolean;
}

interface ChecklistItem {
  label: string;
  done: boolean;
  link?: string;
  linkLabel?: string;
}

export function WelcomeChecklist({ data, isLoading }: WelcomeChecklistProps) {
  const [hidden, setHidden] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "true",
  );

  if (hidden || isLoading) return null;

  const items: ChecklistItem[] = [
    { label: "Account erstellt", done: true },
    {
      label: "Produkte importieren",
      done: (data?.products.total ?? 0) > 0,
      link: "/import",
      linkLabel: "Zum Import",
    },
    {
      label: "Quiz erstellen",
      done: (data?.quizzes.total ?? 0) > 0,
      link: "/quizzes",
      linkLabel: "Zu Quizzes",
    },
    {
      label: "Zuordnung festlegen",
      done: (data?.quizzes.total ?? 0) > 0 && (data?.products.total ?? 0) > 0,
      link: "/quizzes",
      linkLabel: "Zur Zuordnung",
    },
    {
      label: "Quiz einbetten",
      done: (data?.quizzes.published ?? 0) > 0,
      link: "/embed",
      linkLabel: "Zur Einbettung",
    },
  ];

  const allDone = items.every((item) => item.done);
  if (allDone) return null;

  const completedCount = items.filter((item) => item.done).length;
  const remainingCount = items.length - completedCount;

  function handleHide() {
    localStorage.setItem(STORAGE_KEY, "true");
    setHidden(true);
  }

  return (
    <Card className="border-l-4 border-l-teal-500">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <PartyPopper className="h-5 w-5 text-teal-600" />
          <CardTitle className="text-lg">Willkommen bei QuizNudge!</CardTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          Dein Account steht — nur noch {remainingCount}{" "}
          {remainingCount === 1 ? "Schritt" : "Schritte"} bis dein Quiz live
          ist.
        </p>
      </CardHeader>
      <CardContent className="pb-3">
        <ul className="space-y-3">
          {items.map((item) => (
            <li key={item.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {item.done ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                ) : (
                  <Circle className="h-5 w-5 text-muted-foreground shrink-0" />
                )}
                <span
                  className={
                    item.done
                      ? "text-muted-foreground line-through"
                      : "font-medium"
                  }
                >
                  {item.label}
                </span>
              </div>
              {!item.done && item.link && (
                <Button
                  variant="link"
                  size="sm"
                  asChild
                  className="text-teal-600 px-0"
                >
                  <Link to={item.link}>{item.linkLabel}</Link>
                </Button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter className="flex items-center justify-between pt-0">
        <p className="text-xs text-muted-foreground">
          Geschätzte Zeit: ~10 Minuten
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="text-xs text-muted-foreground"
          onClick={handleHide}
        >
          Checkliste verbergen
        </Button>
      </CardFooter>
    </Card>
  );
}
